import React from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { PageHeader } from '../components/PageHeader'
import { DashboardCard } from '../components/DashboardCard'
import { TabNavigation } from '../components/TabNavigation'

interface AttendanceSuccessState {
  date: string
  totalStudents: number
  presentCount: number
  absentCount: number
  lateCount: number
}

export const AttendanceSuccessPage: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const state = location.state as AttendanceSuccessState

  if (!state) {
    return (
      <div className="min-h-screen bg-gray-50 pb-20">
        <PageHeader 
          title="ไม่พบข้อมูล"
          subtitle="ข้อมูลการเช็คชื่อไม่ถูกต้อง"
        />
        <div className="px-4 py-6">
          <DashboardCard title="เกิดข้อผิดพลาด">
            <div className="text-center py-8">
              <span className="text-4xl mb-4 block">❌</span>
              <p className="text-gray-600 mb-4">ไม่พบข้อมูลการเช็คชื่อ</p>
              <button 
                onClick={() => navigate('/admin/attendance')}
                className="btn-primary"
              >
                กลับไปเช็คชื่อ
              </button>
            </div>
          </DashboardCard>
        </div>
        <TabNavigation activeTab="attendance" />
      </div>
    )
  }

  const formatThaiDate = (dateString: string) => {
    const date = new Date(dateString)
    const thaiYear = date.getFullYear() + 543
    const months = [
      'ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.',
      'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'
    ]
    const days = ['อาทิตย์', 'จันทร์', 'อังคาร', 'พุธ', 'พฤหัสบดี', 'ศุกร์', 'เสาร์']
    
    return `วัน${days[date.getDay()]}ที่ ${date.getDate()} ${months[date.getMonth()]} ${thaiYear}`
  }

  const attendanceRate = Math.round((state.presentCount / state.totalStudents) * 100)

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <PageHeader 
        title="✅ บันทึกการเช็คชื่อสำเร็จ"
        subtitle={formatThaiDate(state.date)}
        statusIndicator={{
          status: 'success',
          message: 'การเช็คชื่อได้รับการบันทึกเรียบร้อยแล้ว ระบบจะแจ้งผู้ปกครองโดยอัตโนมัติ'
        }}
      />
      
      <div className="px-4 py-6 space-y-6">
        {/* Success Message */}
        <DashboardCard title="🎉 เช็คชื่อเสร็จสิ้น">
          <div className="text-center py-6">
            <div className="text-6xl mb-4">✅</div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              บันทึกการเช็คชื่อสำเร็จ!
            </h3>
            <p className="text-gray-600 mb-4">
              ระบบได้บันทึกการเช็คชื่อวันนี้เรียบร้อยแล้ว
            </p>
            <div className="text-sm text-gray-500">
              บันทึกเมื่อ: {new Date().toLocaleString('th-TH', {
                timeZone: 'Asia/Bangkok',
                year: 'numeric',
                month: 'long', 
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </div>
          </div>
        </DashboardCard>

        {/* Attendance Summary */}
        <DashboardCard title="📊 สรุปการเช็คชื่อวันนี้">
          <div className="space-y-4">
            {/* Overall Stats */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-blue-50 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-blue-600">{state.totalStudents}</div>
                <div className="text-sm text-blue-800">นักเรียนทั้งหมด</div>
              </div>
              <div className="bg-green-50 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-green-600">{attendanceRate}%</div>
                <div className="text-sm text-green-800">อัตราการมาเรียน</div>
              </div>
            </div>

            {/* Detailed Stats */}
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
                <div className="text-lg font-bold text-green-600">{state.presentCount}</div>
                <div className="text-xs text-green-800">มาเรียน</div>
              </div>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-center">
                <div className="text-lg font-bold text-yellow-600">{state.lateCount}</div>
                <div className="text-xs text-yellow-800">มาสาย</div>
              </div>
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-center">
                <div className="text-lg font-bold text-red-600">{state.absentCount}</div>
                <div className="text-xs text-red-800">ขาดเรียน</div>
              </div>
            </div>
          </div>
        </DashboardCard>

        {/* Next Steps */}
        <DashboardCard title="📝 ขั้นตอนถัดไป">
          <div className="space-y-3">
            <div className="flex items-start space-x-3">
              <span className="text-green-500 mt-1">✅</span>
              <div>
                <p className="font-medium text-gray-900">แจ้งผู้ปกครองโดยอัตโนมัติ</p>
                <p className="text-sm text-gray-600">ระบบจะส่งการแจ้งเตือนให้ผู้ปกครองทราบสถานะการเข้าเรียน</p>
              </div>
            </div>
            
            {state.absentCount > 0 && (
              <div className="flex items-start space-x-3">
                <span className="text-yellow-500 mt-1">⚠️</span>
                <div>
                  <p className="font-medium text-gray-900">ติดตามนักเรียนที่ขาดเรียน</p>
                  <p className="text-sm text-gray-600">มีนักเรียน {state.absentCount} คนที่ขาดเรียนวันนี้</p>
                </div>
              </div>
            )}

            <div className="flex items-start space-x-3">
              <span className="text-blue-500 mt-1">📚</span>
              <div>
                <p className="font-medium text-gray-900">โพสต์บทเรียนวันนี้</p>
                <p className="text-sm text-gray-600">อย่าลืมโพสต์บทเรียนและการบ้านให้ผู้ปกครองทราบ</p>
              </div>
            </div>
          </div>
        </DashboardCard>

        {/* Action Buttons */}
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={() => navigate('/admin/lessons/post')}
            className="btn-primary flex items-center justify-center space-x-2"
          >
            <span>📚</span>
            <span>โพสต์บทเรียน</span>
          </button>
          <button
            onClick={() => navigate('/admin/dashboard')}
            className="btn-secondary flex items-center justify-center space-x-2"
          >
            <span>🏠</span>
            <span>กลับหน้าหลัก</span>
          </button>
        </div>

        {/* Quick Actions */}
        <DashboardCard title="⚡ ปฏิบัติการอื่นๆ">
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => navigate('/admin/attendance')}
              className="btn-secondary text-sm"
            >
              📋 เช็คชื่อวันใหม่
            </button>
            <button
              onClick={() => navigate('/admin/students')}
              className="btn-secondary text-sm"
            >
              👥 จัดการนักเรียน
            </button>
          </div>
        </DashboardCard>
      </div>

      <TabNavigation activeTab="attendance" />
    </div>
  )
}